import React from 'react';
import { Modal } from 'antd';
import styled from 'styled-components';
import { ExclamationCircleOutlined, CheckCircleOutlined, RocketOutlined } from '@ant-design/icons';
import { BusinessButton } from '../../buttons/BusinessButton/BusinessButton';

interface PublishAssessmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  assessmentName: string;
  totalStudents: number;
  studentsWithSheets: number;
  isLoading?: boolean;
}

const ModalContent = styled.div`
  padding: 12px 0;
`;

const InfoSection = styled.div`
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  
  &:last-child {
    border-bottom: none;
  }
`;

const StatLabel = styled.span`
  font-size: 15px;
  color: #4b5563;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatValue = styled.span<{ variant?: 'success' | 'warning' | 'default' }>`
  font-size: 18px;
  font-weight: 700;
  
  ${props => {
    if (props.variant === 'success') return `color: #16A34A;`;
    if (props.variant === 'warning') return `color: #F59E0B;`;
    return `color: #1a1a1a;`;
  }}
`;

const WarningBox = styled.div<{ severity: 'warning' | 'info' }>`
  display: flex;
  gap: 12px;
  padding: 16px;
  border-radius: 10px;
  margin: 16px 0;
  
  ${props => props.severity === 'warning' ? `
    background: rgba(245, 158, 11, 0.1);
    border: 1px solid rgba(245, 158, 11, 0.3);
    color: #92400E;
  ` : `
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.3);
    color: #1E40AF;
  `}
  
  .icon {
    font-size: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }
  
  .content {
    flex: 1;
    
    .title {
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 4px;
    }
    
    .description {
      font-size: 13px;
      line-height: 1.5;
      opacity: 0.9;
    }
  }
`;

const ConfirmText = styled.p`
  font-size: 15px;
  color: #374151;
  margin: 16px 0 24px 0;
  font-weight: 500;
  text-align: center;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    
    button {
      width: 100%;
    }
  }
`;

export const PublishAssessmentModal: React.FC<PublishAssessmentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  assessmentName,
  totalStudents,
  studentsWithSheets,
  isLoading = false
}) => {
  const studentsWithoutSheets = totalStudents - studentsWithSheets;
  const coveragePercent = totalStudents > 0 ? Math.round((studentsWithSheets / totalStudents) * 100) : 0;
  const isFullCoverage = studentsWithSheets === totalStudents;
  
  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={560}
      centered
      closable={!isLoading}
      maskClosable={!isLoading}
    >
      <ModalContent>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <RocketOutlined style={{ fontSize: '48px', color: '#1677ff', marginBottom: '12px' }} />
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px 0' }}>
            Publish Assessment?
          </h2>
          <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
            "{assessmentName}"
          </p>
        </div>
        
        <InfoSection>
          <StatRow>
            <StatLabel>
              👥 Total Students
            </StatLabel>
            <StatValue>{totalStudents}</StatValue>
          </StatRow>
          
          <StatRow>
            <StatLabel>
              ✅ Students with Answer Sheets
            </StatLabel>
            <StatValue variant="success">{studentsWithSheets}</StatValue>
          </StatRow>
          
          {studentsWithoutSheets > 0 && (
            <StatRow>
              <StatLabel>
                ⚠️ Students without Answer Sheets
              </StatLabel>
              <StatValue variant="warning">{studentsWithoutSheets}</StatValue>
            </StatRow>
          )}
          
          <StatRow>
            <StatLabel>
              📊 Coverage
            </StatLabel>
            <StatValue variant={isFullCoverage ? 'success' : 'warning'}>
              {coveragePercent}%
            </StatValue>
          </StatRow>
        </InfoSection>
        
        {isFullCoverage ? (
          <WarningBox severity="info">
            <CheckCircleOutlined className="icon" style={{ color: '#16A34A' }} />
            <div className="content">
              <div className="title">✓ Ready to Publish</div>
              <div className="description">
                All students have answer sheets assigned. The assessment is ready to be published.
              </div>
            </div>
          </WarningBox>
        ) : (
          <WarningBox severity="warning">
            <ExclamationCircleOutlined className="icon" style={{ color: '#F59E0B' }} />
            <div className="content">
              <div className="title">⚠️ Incomplete Coverage</div>
              <div className="description">
                {studentsWithoutSheets} student{studentsWithoutSheets !== 1 ? 's' : ''} don't have answer sheets assigned yet. 
                They won't be able to take this assessment until answer sheets are created for them.
              </div>
            </div>
          </WarningBox>
        )}
        
        <ConfirmText>
          Are you sure you want to publish this assessment?
        </ConfirmText>
        
        <ButtonsWrapper>
          <BusinessButton
            variant="secondary"
            businessSize="medium"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </BusinessButton>
          <BusinessButton
            variant="primary"
            businessSize="medium"
            onClick={onConfirm}
            loading={isLoading}
            icon={<RocketOutlined />}
          >
            {isFullCoverage ? 'Publish Assessment' : 'Publish Anyway'}
          </BusinessButton>
        </ButtonsWrapper>
      </ModalContent>
    </Modal>
  );
};


